import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LayoutGroup, MotionConfig, motion, useScroll, useSpring } from "framer-motion";
import { getAboutUsPage } from "../../features/aboutus/aboutusSlice";
import Hero from "./Hero";
import SectionHead from "./SectionHead";
import Journey from "./Journey";
import { AboutBlocks, PromiseValues } from "./AboutBlocks";
import Team from "./Team";
import Brands from "./Brands";
import Gallery from "./Gallery";
import Impact from "./Impact";
import Reviews from "./Reviews";
import "./AboutUs.css";
const isComponent = (c) =>
  typeof c === "function" || (typeof c === "object" && c !== null && "$$typeof" in c);

Object.entries({ Hero, SectionHead, Journey, AboutBlocks, PromiseValues, Team, Brands, Gallery, Impact, Reviews, MotionConfig, LayoutGroup, motion })
  .forEach(([name, c]) => { if (!isComponent(c)) console.error("NOT A COMPONENT:", name, c); });
const NAV = [
  
  ["about", "About us"],
  ["journey", "Journey"],
  ["team", "Our team"],
  ["brands", "Brands"],
  ["gallery", "Gallery"],
  ["impact", "Our impact"],
  ["reviews", "Customer reviews"],
];

const About_Us = () => {
  const dispatch = useDispatch();
 
  const page = useSelector((s) => s.aboutus.page);
  const [current, setCurrent] = useState("about");

  
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });

  useEffect(() => {
    dispatch(getAboutUsPage());
  }, [dispatch]);

  const oldPhotos = useMemo(() => page.gallery.filter((g) => g.category === "old_photo"), [page.gallery]);
  const gallery = useMemo(() => page.gallery.filter((g) => g.category !== "old_photo"), [page.gallery]);

  // only sections that have content get a button
  const has = {
    about: true,
    journey: page.journey.length > 0,
    team: page.team.length > 0,
    brands: page.brands.length > 0,
    gallery: gallery.length > 0,
    impact: page.impact.length > 0,
    reviews: page.reviews.length > 0,
  };
  const nav = NAV.filter(([id]) => has[id]);

 
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setCurrent(e.target.id)),
      { rootMargin: "-45% 0px -50% 0px" }
    );
    NAV.forEach(([id]) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [page]);

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <MotionConfig reducedMotion="user">
      <main className="au-page">
        <Hero />

        <nav className="au-subnav" aria-label="About sections">
          <LayoutGroup id="au-nav">
            <ul>
              {nav.map(([id, label]) => (
                <li key={id}>
                  <button type="button" aria-current={current === id ? "true" : undefined} onClick={() => go(id)}>
                    {current === id && (
                      <motion.span
                        layoutId="au-nav-pill"
                        className="au-subnav__pill"
                        transition={{ type: "spring", stiffness: 380, damping: 32 }}
                      />
                    )}
                    <span>{label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </LayoutGroup>
          <motion.span className="au-subnav__progress" style={{ scaleX: progress }} aria-hidden="true" />
        </nav>

        <section id="about" className="au-sec au-sec--pale">
          <div className="au-wrap">
            <SectionHead eyebrow="Who we are" title="About us" lede="Where eye care, craftsmanship and technology meet." />
            <AboutBlocks sections={page.sections} />
            <PromiseValues />
          </div>
        </section>

        <Journey steps={page.journey} photos={oldPhotos} />
        <Team team={page.team} />
        <Brands brands={page.brands} />
        <Gallery items={gallery} />
        <Impact stats={page.impact} />
        <Reviews reviews={page.reviews} />
      </main>
    </MotionConfig>
  );
};

export default About_Us;